/**
 * WorkshopView: the Workshop as projects (07 Workshop.pen, approved 2 Oct 2026).
 *
 * The sidebar (New chat, Search, the projects with their chats) sits beside
 * one main area:
 * - no params: a new chat, the greeting and the project picker;
 * - `?view=projects`: every project as a tile;
 * - `?view=creos`: every Creo (skill or agent) as a card;
 * - `?projectId=`: the project's home, its panel beside it;
 * - `&threadId=`: a chat in it (`new` = a chat that is starting).
 * A Trends or References Recreate (its opening message in navigation state)
 * lands on a new chat with the message waiting in the box; `?compose=1` opens
 * New project. Through the compact shell the sidebar fills the screen from the
 * title capsule, the same as on a phone.
 *
 * Archive and leave happen at once with a five second Undo; the toast lives
 * here so it survives moving between pages.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router";
import type { HomeStart } from "../home/HomeComposer";
import { queue } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { Menu, Search, SquarePen } from "lucide-react";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAuthStore } from "@/stores/authStore";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useGetBrands } from "@/hooks/useGetBrands";
import { usePermissions } from "@/hooks/usePermissions";
import type { WorkshopSession } from "@/types/workshop.type";
import { KitModal } from "../components/kit/Modal";
import type { CockpitContext } from "../CockpitLayout";
import { EmptyState } from "../components/ViewStates";
import WorkshopNewChat from "../workshop/components/WorkshopNewChat";
import WorkshopProjectsPage from "../workshop/components/WorkshopProjectsPage";
import WorkshopCreosPage from "../workshop/components/WorkshopCreosPage";
import WorkshopFoldedSidebar from "../workshop/components/WorkshopFoldedSidebar";
import { quickCreateRef } from "../workshop/quickCreate";
import type { WorkshopQuickCreateItem, WorkshopQuickCreateRef } from "@/types/workshop.type";
import WorkshopProjectView from "../workshop/components/WorkshopProjectView";
import WorkshopProjectDialog from "../workshop/components/WorkshopProjectDialog";
import WorkshopSearchDialog from "../workshop/components/WorkshopSearchDialog";
import WorkshopSidebar from "../workshop/components/WorkshopSidebar";
import WorkshopLane from "../workshop/components/WorkshopLane";
import ProjectMembersModal from "../workshop/components/ProjectMembersModal";
import type { WorkshopBootstrapPayload } from "../workshop/components/WorkshopStartComposer";
import { WorkshopUndoProvider, WorkshopUndoToast } from "../workshop/components/WorkshopUndo";
import { WorkshopDeskContext, type WorkshopDeskApi, type WorkshopSurface } from "../workshop/components/workshopDesk";
import { useWorkshopProject } from "../workshop/api/useWorkshopProjects";
import { useCreateProjectFromMessage } from "../workshop/api/useWorkshopProjectWriting";
import { workshopSessionsKey } from "../workshop/api/workshopKeys";
import type { WorkshopOutput } from "../workshop/workshopOutputs";
import type { ReasoningEffort } from "@/types/chat.type";
// The shared skeleton pulse (co-skeleton-fade) lives with the walls.
import "../components/wall.css";
import "../workshop/workshop.css";
import "../workshop/workshopProjects.css";

/** URL value meaning "a chat starting in this project", not a real session id. */
const NEW_THREAD_REQUEST = "new";
const PHONE = "(max-width: 767px)";
/** Same cut as the shell's overlay nav. The title capsule replaces that circle. */
const COMPACT = "(max-width: 1023px)";

type WorkshopSeed = {
  message: string;
  modelId: string;
  reasoningEffort: ReasoningEffort;
  quickCreates: WorkshopQuickCreateRef[];
};

type WorkshopSeedFiles = {
  threadId: string;
  files: File[];
};

const foldKey = (userId: string | null) => `ws-side-folded:${userId ?? "anon"}`;

/** The sidebar's fold, remembered per person on this device. Storage may be off. */
export function readRailFold(userId: string | null): boolean {
  try {
    return window.localStorage.getItem(foldKey(userId)) === "1";
  } catch {
    return false;
  }
}

export function writeRailFold(userId: string | null, folded: boolean): void {
  try {
    window.localStorage.setItem(foldKey(userId), folded ? "1" : "0");
  } catch {
    // Private mode or blocked storage: the fold just is not remembered.
  }
}

export default function WorkshopView() {
  return (
    <WorkshopUndoProvider>
      <WorkshopDesk />
    </WorkshopUndoProvider>
  );
}

function WorkshopDesk() {
  const qc = useQueryClient();
  const cockpit = useOutletContext<CockpitContext | undefined>();
  const { selectedBrandId } = useWorkspaceStore();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const { canUseWorkshop: canWrite } = usePermissions();
  const { data: brands } = useGetBrands();
  const brandName = brands?.find((b) => b.id === selectedBrandId)?.name ?? null;
  const isPhone = useMediaQuery(PHONE);
  const lane = useMediaQuery(COMPACT);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view");
  const projectId = searchParams.get("projectId");
  const threadId = searchParams.get("threadId");
  const composing = searchParams.get("compose") === "1";

  const [seed, setSeed] = useState<WorkshopSeed | null>(null);
  const [seedFiles, setSeedFiles] = useState<WorkshopSeedFiles | null>(null);
  const [startingThread, setStartingThread] = useState(false);
  const [sessionBrandId, setSessionBrandId] = useState(selectedBrandId);
  const [droppedProjectId, setDroppedProjectId] = useState<string | null>(null);
  const [pendingOutput, setPendingOutput] = useState<WorkshopOutput | null>(null);
  const [sideSheetOpen, setSideSheetOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  // New project, and who hears the new id (the picker, Move to, the sidebar).
  const [newProject, setNewProject] = useState<{ onCreated?: (id: string) => void } | null>(() =>
    composing ? {} : null,
  );
  // A new chat started from a project's own New chat keeps that project picked.
  const [homeProjectId, setHomeProjectId] = useState<string | null>(null);
  const [homeKey, setHomeKey] = useState(0);
  const [pickedProjectId, setPickedProjectId] = useState<string | null>(null);
  // Use it on Quick Create: the new chat opens with that pill in its composer.
  const [homeQuickCreates, setHomeQuickCreates] = useState<WorkshopQuickCreateRef[]>([]);
  const [folded, setFolded] = useState(() => readRailFold(userId));
  const [foldUser, setFoldUser] = useState(userId);
  // The fold is per person: a different sign-in reads its own.
  if (foldUser !== userId) {
    setFoldUser(userId);
    setFolded(readRailFold(userId));
  }

  if (sessionBrandId !== selectedBrandId) {
    setSessionBrandId(selectedBrandId);
    setSeed(null);
    setSeedFiles(null);
    setStartingThread(false);
    setDroppedProjectId(projectId);
  }
  const activeProjectId = projectId && projectId !== droppedProjectId ? projectId : null;
  const projectQuery = useWorkshopProject(activeProjectId);
  const projectDetail = projectQuery.data?.project ?? null;
  const fromMessage = useCreateProjectFromMessage(selectedBrandId);

  const location = useLocation();
  const [draftPrompt, setDraftPrompt] = useState(
    () => (location.state as { draftPrompt?: string } | null)?.draftPrompt ?? "",
  );

  const setParams = useCallback(
    (mutate: (next: URLSearchParams) => void, replace = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  const clearStart = useCallback(() => {
    setSeed(null);
    setSeedFiles(null);
    setStartingThread(false);
  }, []);

  const newChat = useCallback(
    (pid: string | null = null) => {
      clearStart();
      setDraftPrompt("");
      setHomeQuickCreates([]);
      setHomeProjectId(pid);
      setHomeKey((k) => k + 1);
      setParams((next) => {
        next.delete("view");
        next.delete("projectId");
        next.delete("threadId");
        next.delete("compose");
      });
    },
    [clearStart, setParams],
  );

  const openProject = useCallback(
    (id: string, thread: string | null = null) => {
      setDroppedProjectId(null);
      setParams((next) => {
        next.delete("view");
        next.delete("compose");
        next.set("projectId", id);
        if (thread) next.set("threadId", thread);
        else next.delete("threadId");
      });
    },
    [setParams],
  );

  const openPage = useCallback(
    (page: "projects" | "creos") => {
      clearStart();
      setParams((next) => {
        next.set("view", page);
        next.delete("projectId");
        next.delete("threadId");
        next.delete("compose");
      });
    },
    [clearStart, setParams],
  );
  const openProjects = useCallback(() => openPage("projects"), [openPage]);
  const openCreos = useCallback(() => openPage("creos"), [openPage]);

  const startQuickCreate = useCallback(
    (item: WorkshopQuickCreateItem, pid: string | null) => {
      newChat(pid);
      setHomeQuickCreates([quickCreateRef(item)]);
    },
    [newChat],
  );

  const handleThreadChange = useCallback(
    (thread: string | null) => {
      setStartingThread(false);
      setParams((next) => {
        if (thread) next.set("threadId", thread);
        else next.delete("threadId");
      }, true);
    },
    [setParams],
  );

  useEffect(() => {
    if (!droppedProjectId || projectId !== droppedProjectId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- newChat navigates (setSearchParams) off a project a brand switch dropped; a navigation cannot run during render
    newChat();
  }, [droppedProjectId, projectId, newChat]);

  useEffect(() => {
    if (!composing) return;
    setParams((next) => {
      next.delete("compose");
    }, true);
  }, [composing, setParams]);

  // ⌘K / Ctrl K opens Search from anywhere in the Workshop.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // A chat arriving in the URL ends "starting a chat".
  const [seenThreadId, setSeenThreadId] = useState(threadId);
  if (threadId !== seenThreadId) {
    setSeenThreadId(threadId);
    if (threadId) setStartingThread(false);
  }

  /** The first message goes into a new chat in the project. */
  const startThread = useCallback(
    async (pid: string | null, payload: WorkshopBootstrapPayload) => {
      // No project yet: M7 names one from the message (or the first pill's name) first.
      const naming = payload.firstMessage || payload.quickCreates?.[0]?.name || "";
      const target = pid ?? (await fromMessage.mutateAsync(naming)).id;
      setDraftPrompt("");
      setHomeQuickCreates([]);
      setStartingThread(true);
      setSeed({
        message: payload.firstMessage,
        modelId: payload.modelId,
        reasoningEffort: payload.reasoningEffort,
        quickCreates: payload.quickCreates ?? [],
      });
      openProject(target, NEW_THREAD_REQUEST);
    },
    [fromMessage, openProject],
  );

  /** A file on the opening composer: the chat starts, then uploads it. */
  const startWithFiles = useCallback(
    (pid: string, files: File[]) => {
      void axiosInstance
        .post<{ session: WorkshopSession }>(`/workshop/projects/${pid}/sessions`)
        .then((res) => {
          const session = res.data.session;
          qc.setQueryData<WorkshopSession[]>(workshopSessionsKey(pid), (list) =>
            list?.some((s) => s.id === session.id) ? list : [session, ...(list ?? [])],
          );
          setDraftPrompt("");
          setSeedFiles({ threadId: session.id, files });
          openProject(pid, session.id);
        });
    },
    [qc, openProject],
  );

  /* A chat started on Home (a message, or files for a project): run it once
     through the same start path, then drop it from history so Back or a
     refresh never starts it again. */
  const navigate = useNavigate();
  const homeStart = (location.state as { homeStart?: HomeStart } | null)?.homeStart ?? null;
  const homeStartRan = useRef<HomeStart | null>(null);
  useEffect(() => {
    if (!homeStart || homeStartRan.current === homeStart || !selectedBrandId) return;
    homeStartRan.current = homeStart;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    if (homeStart.kind === "files") startWithFiles(homeStart.projectId, homeStart.files);
    else
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot hand-off of a start made on Home; startThread navigates
      void startThread(homeStart.projectId, homeStart.payload).catch((error) => {
        setDraftPrompt(homeStart.payload.firstMessage);
        queue.add({ title: "Couldn't start the chat", description: getAiRequestErrorMessage(error), variant: "error" });
      });
  }, [homeStart, selectedBrandId, navigate, location.pathname, location.search, startThread, startWithFiles]);

  const toggleFold = useCallback(() => {
    setFolded((current) => {
      const next = !current;
      writeRailFold(userId, next);
      return next;
    });
  }, [userId]);

  const desk = useMemo<WorkshopDeskApi | null>(
    () =>
      selectedBrandId
        ? {
            brandId: selectedBrandId,
            brandName,
            activeProjectId,
            activeThreadId: threadId && threadId !== NEW_THREAD_REQUEST ? threadId : null,
            pickedProjectId,
            setPickedProjectId,
            newChat: (pid) => newChat(pid ?? null),
            openProjects,
            openCreos,
            openProject: (id) => {
              clearStart();
              openProject(id);
            },
            openThread: (pid, tid) => {
              clearStart();
              openProject(pid, tid);
            },
            openNewProject: (onCreated) => setNewProject({ onCreated }),
            openSearch: () => setSearchOpen(true),
            startQuickCreate,
            renamingThreadId,
            startRename: (id) => {
              if (folded) toggleFold();
              setRenamingThreadId(id);
            },
            endRename: () => setRenamingThreadId(null),
          }
        : null,
    [
      selectedBrandId,
      brandName,
      activeProjectId,
      threadId,
      pickedProjectId,
      newChat,
      openProjects,
      openCreos,
      startQuickCreate,
      openProject,
      clearStart,
      renamingThreadId,
      folded,
      toggleFold,
    ],
  );

  if (!selectedBrandId || !desk) {
    return (
      <EmptyState
        title="No brand selected"
        body="Pick a brand from the workspace switcher to open its workshop."
      />
    );
  }

  const surface: WorkshopSurface = activeProjectId
    ? threadId || seed || startingThread
      ? "thread"
      : "project"
    : view === "projects"
      ? "projects"
      : view === "creos"
        ? "creos"
        : "home";
  const showSidebar = !lane && !folded;

  return (
    <WorkshopDeskContext.Provider value={desk}>
      <div className="ws-shell ws-shell--b" data-surface={surface} data-side={lane ? undefined : showSidebar ? "open" : "folded"}>
        {/* Both stay mounted: the shell's width eases between 272 and 56 while
            the open sidebar's rows and the folded strip's icons fade in one
            after another. The hidden one is inert. */}
        {!lane ? (
          <div className="ws-side-shell" data-folded={folded || undefined}>
            <div className="ws-side-pane ws-side-pane--open" inert={folded}>
              <WorkshopSidebar variant="desk" surface={surface} onFold={toggleFold} />
            </div>
            <div className="ws-side-pane ws-side-pane--fold" inert={!folded}>
              <WorkshopFoldedSidebar
                surface={surface}
                projectName={projectDetail?.name ?? null}
                onUnfold={toggleFold}
              />
            </div>
          </div>
        ) : null}
        <div className="ws-main">
          {lane && (surface === "home" || surface === "projects" || surface === "creos") ? (
            <WorkshopLane
              icon={Menu}
              label="Workshop"
              capsuleLabel="Workshop. Open the sidebar"
              onCapsule={() => setSideSheetOpen(true)}
              action={
                surface === "home"
                  ? { icon: SquarePen, label: "New chat", onPress: () => newChat() }
                  : { icon: Search, label: "Search", onPress: () => setSearchOpen(true) }
              }
            />
          ) : null}

          {surface === "home" ? (
            <WorkshopNewChat
              key={`${selectedBrandId}:${homeKey}`}
              initialProjectId={homeProjectId}
              initialText={draftPrompt}
              initialQuickCreates={homeQuickCreates}
              canWrite={canWrite}
              onStart={startThread}
              onStartWithFiles={startWithFiles}
            />
          ) : null}

          {surface === "projects" ? <WorkshopProjectsPage /> : null}

          {surface === "creos" ? <WorkshopCreosPage /> : null}

          {activeProjectId ? (
            <WorkshopProjectView
              key={`${selectedBrandId}:${activeProjectId}`}
              brandId={selectedBrandId}
              projectId={activeProjectId}
              threadId={threadId}
              onThreadChange={handleThreadChange}
              seedMessage={seed?.message ?? null}
              seedModelId={seed?.modelId ?? null}
              seedReasoningEffort={seed?.reasoningEffort ?? null}
              seedQuickCreates={seed?.quickCreates ?? null}
              onSeedConsumed={() => setSeed(null)}
              seedFiles={seedFiles && seedFiles.threadId === threadId ? seedFiles.files : null}
              seedThreadId={seedFiles?.threadId ?? null}
              onSeedFilesConsumed={() => setSeedFiles(null)}
              home={surface === "project"}
              onStartThread={(payload) => startThread(activeProjectId, payload)}
              onStartWithFiles={(files) => startWithFiles(activeProjectId, files)}
              onOpenPeople={() => setPeopleOpen(true)}
              onOpenThread={(id) => openProject(activeProjectId, id)}
              onBackToProjects={openProjects}
              pendingOutput={pendingOutput?.projectId === activeProjectId ? pendingOutput : null}
              onPendingOutputConsumed={() => setPendingOutput(null)}
              isPhone={isPhone}
              lane={lane}
              onOpenSidebar={() => setSideSheetOpen(true)}
              onThreadGone={() => openProject(activeProjectId)}
            />
          ) : null}

          <WorkshopUndoToast />
        </div>

        {lane ? (
          <KitModal
            isOpen={sideSheetOpen}
            onOpenChange={setSideSheetOpen}
            aria-label="Workshop"
            hideCloseButton
            modalClassName="ws-side-sheet"
            overlayClassName="ws-side-sheet-overlay"
          >
            <WorkshopSidebar
              variant="sheet"
              surface={surface}
              onClose={() => setSideSheetOpen(false)}
              onOpenAppNav={
                cockpit
                  ? (trigger) => {
                      setSideSheetOpen(false);
                      cockpit.openNav(trigger);
                    }
                  : undefined
              }
            />
          </KitModal>
        ) : null}

        <WorkshopProjectDialog
          brandId={selectedBrandId}
          isOpen={newProject !== null}
          onOpenChange={(open) => {
            if (!open) setNewProject(null);
          }}
          onCreated={(id) => {
            const heard = newProject?.onCreated;
            setNewProject(null);
            if (heard) heard(id);
            else openProject(id);
          }}
        />

        <WorkshopSearchDialog
          isOpen={searchOpen}
          onOpenChange={setSearchOpen}
          isPhone={isPhone}
          onOpenOutput={(output) => {
            clearStart();
            setPendingOutput(output);
            openProject(output.projectId);
          }}
        />

        {activeProjectId && projectDetail ? (
          <ProjectMembersModal
            projectId={activeProjectId}
            projectName={projectDetail.name}
            isOwner={projectDetail.isOwner}
            isOpen={peopleOpen}
            onOpenChange={setPeopleOpen}
          />
        ) : null}
      </div>
    </WorkshopDeskContext.Provider>
  );
}
